/** A rule this post failed, taken from `get_results`. */
export interface BrokenRule {
  statement: string;
  type?: 'do' | 'dont';
  explanation?: string;
  /** Id of the question this result scored, when the payload sent one. */
  questionId?: string;
}

/** A question from the brief, reduced to the fields needed to name its rule type. */
export interface QuestionRuleType {
  id: string;
  statement: string;
  subjectiveType: number;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Failed rules from a `get_results` payload.
 *
 * An approved row is not a broken rule. A row with no statement is skipped.
 */
export function brokenRules(response: unknown): BrokenRule[] {
  if (!isRecord(response) || !Array.isArray(response['reviewResults'])) {
    return [];
  }
  const rules: BrokenRule[] = [];
  for (const row of response['reviewResults']) {
    if (!isRecord(row)) {
      continue;
    }
    const answer = typeof row['answer'] === 'string' ? row['answer'].trim() : '';
    if (!answer || answer.toLowerCase() === 'approved') {
      continue;
    }
    const statement = typeof row['statement'] === 'string' ? row['statement'].trim() : '';
    if (!statement) {
      continue;
    }
    const typeRaw = typeof row['type'] === 'string' ? row['type'].trim().toLowerCase() : '';
    const type = typeRaw === 'do' || typeRaw === 'dont' ? typeRaw : undefined;
    const explanation = typeof row['explanation'] === 'string' ? row['explanation'].trim() : '';
    const questionId = typeof row['id'] === 'string' ? row['id'].trim() : '';
    rules.push({
      statement,
      ...(type ? { type } : {}),
      ...(explanation ? { explanation } : {}),
      ...(questionId ? { questionId } : {}),
    });
  }
  return rules;
}

/**
 * Subjective or Objective, from the question specification for each failed rule.
 *
 * The review row does not carry this. A question id match wins; the statement is the fallback.
 * Repeated types are listed once.
 */
export function brokenRuleTypeLabels(
  failed: readonly BrokenRule[],
  questions: readonly QuestionRuleType[],
): string[] {
  const byId = new Map(questions.map((question) => [question.id, question.subjectiveType]));
  const byStatement = new Map(questions.map((question) => [question.statement, question.subjectiveType]));
  const labels: string[] = [];
  for (const rule of failed) {
    const subjectiveType = rule.questionId && byId.has(rule.questionId)
      ? byId.get(rule.questionId)
      : byStatement.get(rule.statement);
    const label = subjectiveType === 1 ? 'Subjective' : subjectiveType === 0 ? 'Objective' : undefined;
    if (label && !labels.includes(label)) {
      labels.push(label);
    }
  }
  return labels;
}

/** Explanations for the failed rules, in the same order as those rules. */
export function brokenRuleFindings(rules: readonly BrokenRule[]): string[] {
  return rules.flatMap((rule) => (rule.explanation ? [rule.explanation] : []));
}

export function brokenRuleCaption(type: BrokenRule['type']): string {
  if (type === 'do') {
    return 'Required rule not met';
  }
  if (type === 'dont') {
    return 'Restriction breached';
  }
  return 'Rule';
}
